<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElDropdown, ElDropdownMenu, ElDropdownItem, ElMessageBox } from 'element-plus'
import { ArrowDown, Lock, SwitchButton, Fold, Expand, Warning, ArrowRight,
  OfficeBuilding, ChatDotRound, Briefcase, Search, Money, DataAnalysis,
  Setting, Monitor, MapLocation, User, UserFilled, Connection, FolderOpened,
  Notebook, DocumentCopy, Reading, Menu as MenuIcon } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'
import { useMenuStore } from '@/stores/menu'
import { useEnumStore } from '@/stores/enum'
import CircleMarquee from '@/components/CircleMarquee.vue'
import { circleNewCount, startCircleCountPolling } from '@/api/modules/circle'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const menuStore = useMenuStore()
const enumStore = useEnumStore()

// 侧边栏收缩状态
const sidebarCollapsed = ref(false)

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

// 菜单展开状态（按菜单名称管理，DB 菜单与内置菜单同名兼容）
const openMenus = ref([])

// 默认只展开这三个一级菜单，其余收缩
const DEFAULT_OPEN_MENUS = ['互动交流', '案件处理', '案件查询']

function toggleMenu(name) {
  const idx = openMenus.value.indexOf(name)
  if (idx >= 0) openMenus.value.splice(idx, 1)
  else openMenus.value.push(name)
}

// 一级菜单图标映射（按菜单名称，未匹配时用默认图标）
const MENU_ICONS = {
  基础信息: OfficeBuilding,
  互动交流: ChatDotRound,
  案件处理: Briefcase,
  案件查询: Search,
  财务管理: Money,
  数据看板: DataAnalysis,
  系统设置: Setting,
  中心管理: Monitor,
  站点管理: MapLocation,
  律师事务: User,
  人员信息: UserFilled,
  顾问单位: Connection,
  委托单位: FolderOpened,
  委托人员: UserFilled,
  更新日志: Notebook,
  模板管理: DocumentCopy,
  文书展示: Reading,
}
function menuIcon(name) {
  return MENU_ICONS[name] || MenuIcon
}

// 当前用户信息
const displayName = computed(() => {
  const role = enumStore.getTextByCode('enumUserRole', userStore.userRole)
  const name = userStore.userName
  return role ? `${role} ${name}` : name
})

// 检查菜单项是否可见（角色过滤）
function isMenuVisible(item) {
  if (!item.role_list) return true
  return item.role_list.includes(userStore.userRole)
}

// 点击菜单项
function handleMenuClick(menuIndex, subIndex, submenu) {
  menuStore.setActiveMenu(menuIndex, subIndex)
  const routeConfig = { path: submenu.path }
  if (submenu.query) {
    routeConfig.query = submenu.query
  }
  router.push(routeConfig)
}

// 判断菜单项是否激活
function isMenuActive(menuIndex, subIndex) {
  return menuStore.activeMenuIndex === menuIndex && menuStore.activeSubMenuIndex === subIndex
}

// 修改密码
function handleChangePassword() {
  router.push('/change-password')
}

// 退出登录
function handleLogout() {
  ElMessageBox.confirm('确认退出登录？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
  })
    .then(() => {
      userStore.logout()
    })
    .catch(() => {})
}

// 检查更新（清除缓存并重新加载，保留登录token）
function handleCheckUpdate() {
  ElMessageBox.confirm(
    '确定要检查更新吗？系统将清除缓存并重新加载，当前登录状态将保留。',
    '检查更新',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }
  )
    .then(() => {
      // 先保存登录数据
      const loginData = localStorage.getItem('login_data')

      // 清除缓存（保留登录数据）
      const keysToKeep = ['login_data']
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key)
        }
      })

      // 清除sessionStorage
      sessionStorage.clear()

      // 如果之前有登录数据，确保它还在
      if (loginData) {
        localStorage.setItem('login_data', loginData)
      }

      // 强制刷新页面（绕过浏览器缓存）
      window.location.href = window.location.href.split('?')[0] + '?_=' + Date.now()
    })
    .catch(() => {})
}

// 初始化
onMounted(async () => {
  menuStore.loadFromStorage()
  // 静默加载 DB 菜单（数据表驱动，失败/超时自动降级内置菜单，不阻塞渲染）
  menuStore.loadMenusFromDb()
  if (!enumStore.loaded) {
    try {
      await enumStore.fetchEnumData()
    } catch {
      // 枚举加载失败不阻塞页面
    }
  }
  // 启动律师圈子菜单角标轮询
  startCircleCountPolling()
})

// 监听路由变化，自动高亮菜单
watch(
  () => route.fullPath,
  () => {
    menuStore.setActiveByRoute(route.path, route.query)
  },
  { immediate: true },
)

// 默认展开指定菜单组
onMounted(() => {
  openMenus.value = [...DEFAULT_OPEN_MENUS]
})
</script>

<template>
  <div class="main-layout">
    <!-- 顶栏 -->
    <header class="layout-header">
      <div class="header-left">
        <span class="system-title">常熟律管系统V5</span>
      </div>
      <div class="header-right" :class="{ 'header-locked': userStore.pwdSecurity.forced }">
        <el-button
          class="check-update-btn"
          @click="handleCheckUpdate"
        >
          检查更新
        </el-button>
        <ElDropdown
          trigger="click"
          @command="(cmd) => (cmd === 'password' ? handleChangePassword() : handleLogout())"
        >
          <span class="user-dropdown">
            欢迎：<b>{{ displayName }}</b>
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem command="password">
                <el-icon><Lock /></el-icon>修改密码
              </ElDropdownItem>
              <ElDropdownItem command="logout" divided>
                <el-icon><SwitchButton /></el-icon>退出登录
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </header>

    <!-- 密码安全提示条 -->
    <!-- 弱密码强制修改提示条 -->
    <div
      v-if="userStore.pwdSecurity.forced"
      class="pwd-security-bar danger"
    >
      <el-icon><Warning /></el-icon>
      <span>检测到您的密码强度不足，存在安全风险，请立即修改密码</span>
      <a class="action-link" @click="handleChangePassword">立即修改</a>
    </div>

    <!-- 圈子新动态滚动播报（近 10 分钟有新内容才显示，点击进圈子） -->
    <CircleMarquee />

    <div class="layout-body">
      <!-- 侧边栏 -->
      <aside class="layout-sidebar" :class="{ collapsed: sidebarCollapsed, 'sidebar-locked': userStore.pwdSecurity.forced }">
        <div class="sidebar-toggle" @click="toggleSidebar">
          <el-icon :size="18"><Expand v-if="sidebarCollapsed" /><Fold v-else /></el-icon>
        </div>
        <div v-show="!sidebarCollapsed" class="menu-container">
          <template v-for="(menu, menuIndex) in menuStore.menuList" :key="menuIndex">
            <template v-if="!menu.role_list || isMenuVisible(menu)">
              <!-- 菜单标题 -->
              <div
                class="menu-title"
                :class="{ toggle: !openMenus.includes(menu.page_name) }"
                @click="toggleMenu(menu.page_name)"
              >
                <el-icon class="menu-icon"><component :is="menuIcon(menu.page_name)" /></el-icon>
                <span class="menu-name">{{ menu.page_name }}</span>
                <el-icon
                  v-if="menu.page_list && menu.page_list.length"
                  class="menu-arrow"
                  :class="{ open: openMenus.includes(menu.page_name) }"
                ><ArrowRight /></el-icon>
              </div>
              <!-- 子菜单 -->
              <template v-if="openMenus.includes(menu.page_name)">
                <div
                  v-for="(submenu, subIndex) in menu.page_list"
                  v-show="isMenuVisible(submenu)"
                  :key="subIndex"
                  class="menu-item"
                  :class="{ active: isMenuActive(menuIndex, subIndex) }"
                  @click="handleMenuClick(menuIndex, subIndex, submenu)"
                >
                  <span>{{ submenu.page_name }}</span>
                  <span v-if="submenu.path === '/circle/lawyer-circle' && circleNewCount > 0" class="menu-badge">{{ circleNewCount > 99 ? '99+' : circleNewCount }}</span>
                </div>
              </template>
            </template>
          </template>
        </div>
        <!-- 收缩状态下的简略菜单图标提示 -->
      </aside>

      <!-- 内容区 -->
      <main class="layout-content">
        <router-view v-slot="{ Component }">
          <keep-alive>
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </main>
    </div>
  </div>
</template>

<style scoped>
.main-layout {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 顶栏 */
.layout-header {
  height: 60px;
  background: linear-gradient(135deg, var(--law-primary) 0%, var(--law-secondary) 100%);
  color: var(--law-white);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 30px;
  flex-shrink: 0;
  z-index: 999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-bottom: 3px solid var(--law-accent);
}

.pwd-security-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 14px;
  flex-shrink: 0;
}

.pwd-security-bar.warn {
  background-color: #fdf6ec;
  color: #e6a23c;
  border-bottom: 1px solid #f5dab1;
}

.pwd-security-bar.danger {
  background-color: #fef0f0;
  color: #f56c6c;
  border-bottom: 1px solid #fde2e2;
}

.pwd-security-bar .countdown {
  color: #f56c6c;
  font-weight: bold;
}

.pwd-security-bar .action-link {
  margin-left: 12px;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}

.pwd-security-bar.warn .action-link {
  color: #e6a23c;
}

.pwd-security-bar.danger .action-link {
  color: #f56c6c;
}

.system-title {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 1px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.check-update-btn {
  color: var(--law-white);
  border-color: rgba(255, 255, 255, 0.5);
  background-color: rgba(255, 255, 255, 0.1);
  font-size: 14px;
  transition: all 0.3s ease;
}

.check-update-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: var(--law-white);
  color: var(--law-white);
}

.user-dropdown {
  color: var(--law-white);
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.user-dropdown:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* 主体 */
.layout-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 侧边栏 */
.layout-sidebar {
  width: 160px;
  background: linear-gradient(180deg, var(--law-primary) 0%, #0f2440 100%);
  color: var(--law-white);
  overflow-y: auto;
  flex-shrink: 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
}

.layout-sidebar.collapsed {
  width: 48px;
  overflow: hidden;
}

.sidebar-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-toggle:hover {
  color: var(--law-white);
  background-color: rgba(255, 255, 255, 0.1);
}

.layout-sidebar::-webkit-scrollbar {
  width: 1px;
}

.menu-title {
  text-align: left;
  padding: 0 12px;
  line-height: 40px;
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--law-white);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  border-left: 3px solid transparent;
  transition: all 0.3s ease;
}

.menu-title:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-left-color: var(--law-accent);
}

.menu-title .menu-icon {
  font-size: 16px;
  color: var(--law-accent-light);
  margin-right: 8px;
  flex-shrink: 0;
}
.menu-title .menu-name {
  flex: 1;
}
.menu-title .menu-arrow {
  font-size: 12px;
  opacity: 0.6;
  transition: transform 0.3s ease;
}
.menu-title .menu-arrow.open {
  transform: rotate(90deg);
}
.menu-title:hover .menu-icon {
  color: var(--law-accent);
}

.menu-item {
  background-color: rgba(255, 255, 255, 0.02);
  color: rgba(255, 255, 255, 0.85);
  text-align: left;
  padding: 0 12px 0 26px;
  line-height: 38px;
  cursor: pointer;
  letter-spacing: 0.5px;
  font-size: 13px;
  border-left: 3px solid transparent;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}
.menu-item::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  margin-right: 8px;
  flex-shrink: 0;
  transition: all 0.3s ease;
}
.menu-item:hover::before,
.menu-item.active::before {
  background: var(--law-accent-light);
  box-shadow: 0 0 4px rgba(183, 121, 31, 0.8);
}
.menu-item > span:first-child { flex: 1; }
.menu-badge {
  flex-shrink: 0;
  margin-left: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  line-height: 18px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #ef4444;
  border-radius: 10px;
  animation: badge-pulse 1.6s ease-in-out infinite;
}
@keyframes badge-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.menu-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: var(--law-white);
  border-left-color: var(--law-accent-light);
}

.menu-item.active {
  background: linear-gradient(90deg, rgba(183, 121, 31, 0.3) 0%, rgba(183, 121, 31, 0.1) 100%);
  color: var(--law-white);
  border-left-color: var(--law-accent);
  font-weight: 600;
}

/* 内容区 */
.layout-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: var(--law-bg);
  padding: 20px;
}

/* 强制修改密码时的锁定样式（仅禁用侧边栏和顶部菜单，内容区保持可操作） */
.sidebar-locked {
  opacity: 0.5;
  pointer-events: none;
  user-select: none;
}

.header-locked {
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
}
</style>
